import { useContext } from 'react';
import { CheckoutContext } from '@/context/CheckoutContext';
import { hasBrandedPayCta } from '@/components/PaymentInputs/brandedPayCta.config';
import type { PaymentMethodEnum } from '@/types/common';

export interface UseBrandedPayCtaResult {
  /**
   * `true` when the calling form should paint an APM-branded submit CTA
   * ({@link BrandedPayCTA}) instead of the neutral "Pay $X.XX"
   * ({@link PayButton}). Both of these must hold:
   *   1. The active template opts into `preferExpressButton` — the same
   *      flag that swaps Amazon Pay's PaymentElement for its Stripe ECE.
   *      Semantics: "on this template, the primary CTA IS the selected
   *      wallet/APM". PREFERRED sets this today; DEFAULT / SINGLE don't.
   *   2. The given payment method is registered in
   *      {@link BRANDED_PAY_CTA_CONFIG} (BLIK / PIX today).
   */
  shouldRenderBrandedCta: boolean;
}

/**
 * Kept as a hook (not a plain util) so it can pull `template` from
 * context — callers avoid an incidental `template` destructure whose
 * only purpose would be to read one feature flag.
 *
 * Returns an object (rather than a bare boolean) so the shape is
 * forward-compatible: additions like the resolved
 * {@link BrandedCtaVisual} or an explanatory `reason` can piggy-back on
 * this hook without a signature break at every caller.
 */
export const useBrandedPayCta = (paymentMethod: PaymentMethodEnum | undefined): UseBrandedPayCtaResult => {
  const { template } = useContext(CheckoutContext);
  return {
    shouldRenderBrandedCta: template.features.preferExpressButton && hasBrandedPayCta(paymentMethod),
  };
};
